import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';

export function LoginButton() {
  const startAuthFlow = useAuthStore((state) => state.startAuthFlow);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined;
  const isConfigured = Boolean(clientId);

  const handleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await startAuthFlow();
      // startAuthFlow redirects — execution stops here on success
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not start sign-in. Please try again.';
      setError(message);
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-300 dark:border-neutral-700 shadow-xl p-8">
        <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 text-center mb-2">
          Sign In
        </h2>
        <p className="text-neutral-500 dark:text-neutral-400 text-center text-sm mb-8">
          Sign in with your QuarkChat account
        </p>

        {!isConfigured && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 mb-4"
            role="alert"
            data-testid="config-error"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">
              Authentication is not configured. <code>VITE_WORKOS_CLIENT_ID</code> is missing.
            </p>
          </div>
        )}

        {error && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 mb-4"
            role="alert"
            data-testid="login-error"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <Button
          type="button"
          data-testid="sign-in-button"
          variant="primary"
          size="lg"
          loading={isLoading}
          disabled={isLoading || !isConfigured}
          onClick={handleSignIn}
          className="w-full"
        >
          {isLoading ? 'Redirecting…' : 'Sign In'}
        </Button>
      </div>
    </div>
  );
}
